import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";

import { TEACHER_SESSION_KEY } from "../../../Definition";
import { useDraggable } from "../../../hooks/useDraggable";
import useOnClickOutside from "../../../hooks/useOnClickOutside";
import Button from "../../../atoms/Button";
import ChapterCard from "../../../molecules/ChapterCard";
import SelectAnswersModal from "../../../molecules/SelectAnswersModal";
import LessonCompleteConfirmModal from "../../../molecules/LessonCompleteConfirmModal";
import VideoLessonGuideModal from "../VideoLessonGuideModal";
import EvaluationLessonModal from "../EvaluationLessonModal";
import ModalThreeRules from "../ModalThreeRules";
import Timer from "../Timer";
import ModalAnswers from "../../../templates/teacher/ModalAnswers";
import ModalEditGroupClass from "../../../templates/teacher/ModalEditGroupClass";
import BAWorkAnswersModal from "../../../templates/teacher/BAWorkAnswersModal";
import { ITeacherLessonRetrospectives, VideoChapter } from "../../../state";
import { ReactComponent as QuestionMark } from "../../../assets/svg/video_guide_question_mark.svg";
import { ReactComponent as BookIcon } from "../../../assets/svg/book_2.svg";
import { ReactComponent as ArrowRightIcon } from "../../../assets/svg/arrow_right_single.svg";

import styles from "./VideoLessonSideMenu.module.scss";

interface VideoLessonSideMenuProps {
  lessonId: number;
  lessonName: string;
  lessonTitle: string;
  teachingMaterialId: number;
  teachingMaterialName: string;
  isDisplayAllSchoolsAnswer: boolean;
  videoChapters: VideoChapter[];
  playingChapterId: number;
  nextChapterId: number | null;
  classList: number[];
  prevLessonId: number | null;
  nextLessonId: number | null;
  hasLessonCompletion: boolean;
  teacherLessonRetrospectives: ITeacherLessonRetrospectives;
  showChapterMenu: boolean;
  postCompletedLesson: () => void;
  postCompletedLessonRetrospectives: (
    score: number,
    lessonDeliveryScore: number,
    groupWorkScore: number | null,
  ) => void;
  prevLesson: () => void;
  nextLesson: () => void;
  setShowChapterMenu: (show: boolean) => void;
  onSwitchToSlide: () => void;
  canSwitchToSlide: boolean;
  isFromProjection?: boolean;
}

export const VideoLessonSideMenu = ({
  lessonId,
  lessonName,
  lessonTitle,
  teachingMaterialId,
  teachingMaterialName,
  isDisplayAllSchoolsAnswer,
  videoChapters,
  playingChapterId,
  nextChapterId,
  classList,
  prevLessonId,
  nextLessonId,
  hasLessonCompletion,
  teacherLessonRetrospectives,
  showChapterMenu,
  postCompletedLesson,
  postCompletedLessonRetrospectives,
  prevLesson,
  nextLesson,
  setShowChapterMenu,
  onSwitchToSlide,
  canSwitchToSlide,
  isFromProjection,
}: VideoLessonSideMenuProps) => {
  const history = useHistory();
  const extra = sessionStorage.getItem(TEACHER_SESSION_KEY)
    ? `&session_id=${sessionStorage.getItem(TEACHER_SESSION_KEY)}`
    : "";
  const works = videoChapters.flatMap(chapter => chapter.works);
  const retrospectiveWorkTitle = [...works]
    .reverse()
    .find(work => work.type === "issue" && work.title.match(/^ワーク\d+/))
    ?.title;

  const [showGuideModal, setShowGuideModal] = useState(false);
  const [
    showLessonCompleteConfirmModal,
    setShowLessonCompleteConfirmModal,
  ] = useState(false);
  const [showEvaluationLessonModal, setShowEvaluationLessonModal] = useState(
    false,
  );
  const [showSelectAnswersModal, setShowSelectAnswersModal] = useState(false);
  const [issueId, setIssueId] = useState<number | null>(null);
  const [oppaId, setOppaId] = useState<number | null>(null);
  const [showThreeRules, setShowThreeRules] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [showModalEditGroupClass, setShowModalEditGroupClass] = useState(false);

  const videoChapterRef = useRef(null);
  useOnClickOutside(videoChapterRef, () => {
    !showGuideModal &&
      !showLessonCompleteConfirmModal &&
      setShowChapterMenu(false);
  });

  const answersButtonRef = useRef<HTMLButtonElement>(null);
  const [answersButtonRect, setAnswersButtonRect] = useState<DOMRect>();

  const playingButton = (id: number) => {
    setShowChapterMenu(false);
    history.push({
      search: `?lesson=${lessonId}&chapter=${id}&classList=${classList}`,
    });
  };

  const showAnswersByWork = (workId: number) => {
    setShowSelectAnswersModal(false);
    const work = works.find(w => w.id === workId);
    if (work?.type === "oppa") {
      setIssueId(null);
      setOppaId(workId);
    } else {
      setOppaId(null);
      setIssueId(workId);
    }
  };

  const {
    position: timerPos,
    onMouseDown: onTimerDragStart,
    reset: resetTimerPos,
  } = useDraggable(0, 0);

  useEffect(() => {
    if (!showTimer) resetTimerPos();
  }, [showTimer, resetTimerPos]);

  useEffect(() => {
    const updateRect = () => {
      setAnswersButtonRect(answersButtonRef.current?.getBoundingClientRect());
    };

    updateRect();
    window.addEventListener("resize", updateRect);
    return () => window.removeEventListener("resize", updateRect);
  }, []);

  return (
    <div ref={videoChapterRef} className={styles.lessonSideMenuWrapper}>
      {showGuideModal && (
        <VideoLessonGuideModal onClose={() => setShowGuideModal(false)} />
      )}
      {showLessonCompleteConfirmModal && (
        <LessonCompleteConfirmModal
          title={"このクラスの授業は終了しましたか？"}
          buttons={[
            {
              title: "いいえ",
              action: () => {
                setShowLessonCompleteConfirmModal(false);
              },
              type: "None",
            },
            {
              title: "はい",
              action: () => {
                setShowLessonCompleteConfirmModal(false);
                setShowEvaluationLessonModal(true);
              },
              type: "Success",
            },
          ]}
          onClose={() => setShowLessonCompleteConfirmModal(false)}
        />
      )}
      {showEvaluationLessonModal && (
        <EvaluationLessonModal
          teacherLessonRetrospectives={teacherLessonRetrospectives}
          onRatingCompleted={(score, lessonDeliveryScore, groupWorkScore) => {
            postCompletedLesson();
            postCompletedLessonRetrospectives(
              score,
              lessonDeliveryScore,
              groupWorkScore,
            );
            setShowEvaluationLessonModal(false);
            window.location.href = `${process.env.REACT_APP_ONE_MENU_URL}/class_retrospect/${classList[0]}?teaching_material_id=${teachingMaterialId}&lesson_id=${lessonId}${extra}`;
          }}
          onClose={() => setShowEvaluationLessonModal(false)}
        />
      )}
      {showChapterMenu && (
        <div className={styles.chapterMenuWrapper}>
          <div className={styles.chapterMenu}>
            <div className={styles.chaptersContainer}>
              <div
                className={styles.videoLessonGuide}
                onClick={() => {
                  setShowGuideModal(true);
                }}
              >
                <QuestionMark />
                <p>動画教材での授業方法</p>
              </div>
              {videoChapters.map(chapter => (
                <ChapterCard
                  key={chapter.id}
                  chapter={chapter}
                  retrospectiveWorkTitle={retrospectiveWorkTitle}
                  isInitialOpen={chapter.id === nextChapterId}
                  isPlaying={chapter.id === playingChapterId}
                  handlePlayingButton={playingButton}
                />
              ))}
            </div>
            {!hasLessonCompletion && (
              <Button
                className={styles.finishLessonButton}
                variant="normal"
                onClick={() => setShowLessonCompleteConfirmModal(true)}
                name="授業を終了する"
              />
            )}
          </div>
        </div>
      )}
      {showSelectAnswersModal && (
        <SelectAnswersModal
          parentPositionTop={
            answersButtonRect ? `${answersButtonRect?.top}px` : `0px`
          }
          parentPositionLeft={
            answersButtonRect ? `${answersButtonRect?.left}px` : `0px`
          }
          lessonName={lessonName}
          lessonTitle={lessonTitle}
          teachingMaterialName={teachingMaterialName}
          works={works}
          retrospectiveWorkTitle={retrospectiveWorkTitle}
          handleClose={() => setShowSelectAnswersModal(false)}
          handleClickWork={showAnswersByWork}
        />
      )}
      {oppaId && (
        <BAWorkAnswersModal
          oppaId={oppaId}
          classList={classList}
          onCloseModal={() => setOppaId(null)}
        />
      )}
      {issueId && (
        <ModalAnswers
          is_display_all_schools_answer={isDisplayAllSchoolsAnswer}
          issueID={issueId}
          classIDs={classList}
          onClose={() => {
            setIssueId(null);
          }}
        />
      )}
      {showThreeRules && (
        <ModalThreeRules
          onClose={() => {
            setShowThreeRules(false);
          }}
        />
      )}
      {showTimer && (
        <div
          className={styles.timer}
          onMouseDown={onTimerDragStart}
          style={{
            transform: `translate(${timerPos.x}px, ${timerPos.y}px)`,
            cursor: "move",
          }}
        >
          <Timer
            className={styles.timer__timerInShareScreen}
            onClose={() => setShowTimer(false)}
          />
        </div>
      )}
      {showModalEditGroupClass && (
        <ModalEditGroupClass
          classIDs={classList}
          onClose={() => setShowModalEditGroupClass(false)}
        />
      )}
      <div
        className={clsx(
          styles.lessonSideMenu,
          isFromProjection && styles.animate,
        )}
      >
        <div className={styles.topButtons}>
          <button
            className={styles.switchButton}
            onClick={onSwitchToSlide}
            disabled={!canSwitchToSlide}
          >
            <BookIcon className={styles.iconBook} />
            スライド
            <ArrowRightIcon className={styles.iconArrow} />
          </button>
          <button
            className={clsx(styles.actionBtn, styles.primary)}
            onClick={() => setShowChapterMenu(!showChapterMenu)}
          >
            動画チャプター
          </button>

          <button
            ref={answersButtonRef}
            className={clsx(
              styles.actionBtn,
              styles.primary,
              showSelectAnswersModal && styles.activeBtnInModal,
            )}
            onClick={() => {
              setShowSelectAnswersModal(!showSelectAnswersModal);
            }}
          >
            みんなの回答
          </button>
          <button
            className={styles.actionBtn}
            onClick={() => {
              setShowThreeRules(!showThreeRules);
            }}
          >
            3つのルール
          </button>
          <button
            className={clsx(styles.actionBtn, styles.mb16)}
            onClick={() => {
              setShowTimer(!showTimer);
            }}
          >
            タイマー
          </button>
          <button
            className={styles.actionBtn}
            onClick={() => {
              setShowModalEditGroupClass(!showModalEditGroupClass);
            }}
          >
            グループ設定
          </button>
        </div>
        <div className={styles.bottomButtons}>
          <div className={styles.navButtons}>
            <button
              className={styles.actionBtn}
              disabled={!prevLessonId}
              onClick={prevLesson}
            >
              前のLesson
            </button>
            <button
              className={styles.actionBtn}
              disabled={!nextLessonId}
              onClick={nextLesson}
            >
              次のLesson
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoLessonSideMenu;
